// Customer-facing list of their quotations, job cards & delivery notes.
import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, FileText, Receipt, Wrench, ClipboardList, CheckCircle2, MessageSquareText, BadgeCheck, Paperclip, FileUp } from "lucide-react";
import { api, apiClient } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { formatCurrency } from "@/lib/format";

const TYPE_META = {
  quotation: { label: "Quotation", icon: Receipt, cls: "bg-emerald-50 text-emerald-700" },
  invoice: { label: "Invoice", icon: Receipt, cls: "bg-rose-50 text-rose-700" },
  job_card: { label: "Job Card", icon: Wrench, cls: "bg-blue-50 text-blue-700" },
  sdn: { label: "Delivery Note", icon: ClipboardList, cls: "bg-amber-50 text-amber-700" },
};

const STATUS_CLS = {
  sent: "bg-blue-100 text-blue-700",
  issued: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
};

export const MyDocuments = () => {
  const { toast } = useToast();
  const [docs, setDocs] = useState(null);
  const [dlId, setDlId] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [changeBox, setChangeBox] = useState({ id: null, comments: "", sending: false });
  const [lpoUploading, setLpoUploading] = useState(null);
  const lpoInput = useRef(null);
  const lpoTarget = useRef(null);

  const pickLpo = (d) => {
    lpoTarget.current = d;
    lpoInput.current?.click();
  };

  const uploadLpo = async (file) => {
    const d = lpoTarget.current;
    if (!file || !d) return;
    const ref = window.prompt("Your LPO number / reference (optional):", d.lpo_reference || "") || "";
    setLpoUploading(d.id);
    try {
      await api.portalUploadLpo(d.id, file, ref.trim());
      toast(`LPO uploaded for ${d.serial} — thank you!`, "success");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "LPO upload failed — please try again", "error");
    } finally {
      setLpoUploading(null);
      if (lpoInput.current) lpoInput.current.value = "";
    }
  };

  const downloadLpo = async (d) => {
    try {
      const res = await apiClient.get(`/portal/documents/${d.id}/lpo`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.lpo_file_name || "LPO";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast("LPO download failed", "error"); }
  };

  const load = useCallback(async () => {
    try { setDocs(await api.listPortalDocuments()); }
    catch { setDocs([]); toast("Could not load your documents", "error"); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const download = async (d) => {
    setDlId(d.id);
    try {
      const res = await apiClient.get(`/portal/documents/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d.serial || "document").replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Download failed — please try again", "error");
    } finally {
      setDlId(null);
    }
  };

  const downloadReceipt = async (d) => {
    setDlId(`rcpt-${d.id}`);
    try {
      const res = await apiClient.get(`/portal/documents/${d.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RCPT_${(d.serial || "receipt").replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Receipt download failed — please try again", "error");
    } finally {
      setDlId(null);
    }
  };

  const accept = async (d) => {
    if (!window.confirm(`Accept quotation ${d.serial}? The Jero-One team will be notified and will start scheduling the work.`)) return;
    setAcceptingId(d.id);
    try {
      await api.portalAcceptDoc(d.id);
      toast(`Quotation ${d.serial} accepted — thank you!`, "success");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not accept — please try again", "error");
    } finally {
      setAcceptingId(null);
    }
  };

  const sendChanges = async (d) => {
    const comments = changeBox.comments.trim();
    if (!comments) { toast("Please describe the changes you need", "error"); return; }
    setChangeBox((c) => ({ ...c, sending: true }));
    try {
      await api.portalRequestChanges(d.id, comments);
      toast("Change request sent — we'll send a revised quotation shortly", "success");
      setChangeBox({ id: null, comments: "", sending: false });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not send — please try again", "error");
      setChangeBox((c) => ({ ...c, sending: false }));
    }
  };

  if (docs === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center" data-testid="portal-docs-loading">
        <Loader2 size={16} className="animate-spin" /> Loading your documents…
      </div>
    );
  }

  return (
    <div data-testid="portal-documents">
      <input
        ref={lpoInput}
        type="file"
        hidden
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
        data-testid="portal-lpo-file-input"
        onChange={(e) => uploadLpo(e.target.files?.[0])}
      />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">My Documents</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Quotations, job cards and delivery notes issued to your email
          </p>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="portal-docs-empty">
          <FileText size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-semibold text-slate-700">No documents yet</p>
          <p className="text-xs text-slate-500 mt-1">
            Documents shared with you by Jero-One will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {docs.map((d) => {
            const meta = TYPE_META[d.doc_type] || TYPE_META.quotation;
            const Icon = meta.icon;
            return (
              <div key={d.id} data-testid={`portal-doc-${d.id}`}>
                <div className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.cls}`}>
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-[#0e6939]">{d.serial}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize ${STATUS_CLS[d.status] || "bg-slate-100 text-slate-600"}`}>
                        {d.status}
                      </span>
                      {d.doc_type === "quotation" && d.status === "accepted" && d.payment_status === "paid" && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-0.5" data-testid={`portal-paid-${d.id}`}>
                          <BadgeCheck size={10} /> Paid
                        </span>
                      )}
                      {d.doc_type === "quotation" && d.status === "accepted" && d.payment_status === "partial" && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`portal-partial-${d.id}`}>
                          Paid {formatCurrency(d.amount_paid || 0, d.currency)} of {formatCurrency(d.total || 0, d.currency)}
                        </span>
                      )}
                      {d.doc_type === "invoice" && d.lpo_file_name && (
                        <button
                          data-testid={`portal-lpo-chip-${d.id}`}
                          onClick={() => downloadLpo(d)}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 flex items-center gap-0.5 hover:bg-sky-200"
                          title={`Download your uploaded LPO (${d.lpo_file_name})`}
                        >
                          <Paperclip size={10} /> LPO{d.lpo_reference ? ` ${d.lpo_reference}` : ""} ✓
                        </button>
                      )}
                    </div>
                    <div className="text-sm font-medium text-slate-900 truncate mt-0.5">{d.title || "—"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.date}
                      {d.total != null && d.total > 0 && (
                        <> · <span className="font-semibold">{formatCurrency(d.total, d.currency)}</span></>
                      )}
                      {d.related_job_card && <> · from {d.related_job_card}</>}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {d.doc_type === "invoice" && (
                      <button
                        data-testid={`portal-upload-lpo-${d.id}`}
                        onClick={() => pickLpo(d)}
                        disabled={lpoUploading === d.id}
                        className={`text-xs font-semibold rounded-md px-2.5 py-1.5 flex items-center gap-1.5 ${
                          d.lpo_file_name
                            ? "text-slate-600 border border-slate-200 hover:border-sky-400 hover:text-sky-700"
                            : "text-white bg-sky-600 hover:bg-sky-700"
                        }`}
                        title={d.lpo_file_name ? "Replace the uploaded LPO" : "Upload your Local Purchase Order for this invoice"}
                      >
                        {lpoUploading === d.id ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />}
                        <span className="hidden sm:inline">{d.lpo_file_name ? "Replace LPO" : "Upload LPO"}</span>
                        <span className="sm:hidden">LPO</span>
                      </button>
                    )}
                    {d.doc_type === "quotation" && (d.status === "sent" || d.status === "issued") && (
                      <>
                        <button
                          data-testid={`portal-accept-${d.id}`}
                          onClick={() => accept(d)}
                          disabled={acceptingId === d.id}
                          className="text-xs font-semibold text-white bg-[#0e6939] hover:bg-[#0b552e] rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
                        >
                          {acceptingId === d.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                          Accept
                        </button>
                        <button
                          data-testid={`portal-changes-${d.id}`}
                          onClick={() => setChangeBox((c) => (c.id === d.id ? { id: null, comments: "", sending: false } : { id: d.id, comments: "", sending: false }))}
                          className="text-xs font-semibold text-slate-600 hover:text-amber-700 border border-slate-200 hover:border-amber-400 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
                        >
                          <MessageSquareText size={13} />
                          <span className="hidden sm:inline">Request changes</span>
                        </button>
                      </>
                    )}
                    {d.doc_type === "quotation" && d.payment_status === "paid" && (
                      <button
                        data-testid={`portal-receipt-${d.id}`}
                        onClick={() => downloadReceipt(d)}
                        disabled={dlId === `rcpt-${d.id}`}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 border border-emerald-200 hover:border-emerald-400 bg-emerald-50 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
                      >
                        {dlId === `rcpt-${d.id}` ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
                        Receipt
                      </button>
                    )}
                    <button
                      data-testid={`portal-dl-${d.id}`}
                      onClick={() => download(d)}
                      disabled={dlId === d.id}
                      className="text-xs font-semibold text-slate-600 hover:text-[#0e6939] border border-slate-200 hover:border-[#0e6939] rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
                    >
                      {dlId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                      <span className="hidden sm:inline">PDF</span>
                    </button>
                  </div>
                </div>
                {changeBox.id === d.id && (
                  <div className="px-4 pb-4 -mt-1" data-testid={`portal-changes-box-${d.id}`}>
                    <textarea
                      data-testid={`portal-changes-comments-${d.id}`}
                      rows={3}
                      className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-[#0e6939]"
                      placeholder="e.g. Please revise the quantity on item 2 and extend the delivery timeline…"
                      value={changeBox.comments}
                      onChange={(e) => setChangeBox((c) => ({ ...c, comments: e.target.value }))}
                    />
                    <div className="flex justify-end gap-2 mt-1.5">
                      <button
                        className="text-xs font-semibold text-slate-500 px-2.5 py-1.5"
                        onClick={() => setChangeBox({ id: null, comments: "", sending: false })}
                      >
                        Cancel
                      </button>
                      <button
                        data-testid={`portal-changes-send-${d.id}`}
                        onClick={() => sendChanges(d)}
                        disabled={changeBox.sending}
                        className="text-xs font-semibold text-white bg-[#0e6939] hover:bg-[#0b552e] rounded-md px-3 py-1.5 flex items-center gap-1.5"
                      >
                        {changeBox.sending ? <Loader2 size={13} className="animate-spin" /> : null}
                        Send request
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
